import React, { useMemo, useState, useEffect } from "react";
import { Modal, Tooltip } from "antd";
import { DataIndexTableSearchableContainer } from "../data-index-table-modal.styles";
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    type MRT_RowSelectionState,
} from "material-react-table";
import { EPSG_INdexData } from "../../../../common/EPSGIndexUtils";


interface ChildComponentProps {
    onClose: () => void;
    title: React.ReactNode;
    titleColor?: string;
    titleFontSize?: string;
    setSelectedRow: React.Dispatch<React.SetStateAction<EPSG_INdexData>>;
    data: any[];
}

const columnConfig = [
    { key: "name", size: 250 },
    { key: "code", size: 100 },
    { key: "area", size: 300 },
    { key: "bbox", size: 220 },
    { key: "kind", size: 100 },
    { key: "proj4", size: 100 },
    { key: "unit", size: 100 },
    { key: "wkt", size: 50 },
];

const InfoIcon: React.FC<{ info: string; onInfoClick: () => void }> = ({
    info,
    onInfoClick,
}) => {
    const handleInfoClick = () => {
        onInfoClick();
    };

    return (
        <>
            <Tooltip title="Info">
                <span>
                    <div className="info-icon-container" onClick={handleInfoClick}>
                        <span>ℹ️</span>
                    </div>
                </span>
            </Tooltip>
        </>
    );
};

export const SearchableTable: React.FC<ChildComponentProps> = ({
    onClose,
    title,
    titleColor,
    titleFontSize,
    setSelectedRow,
    data
}) => {
    // const [data, setData] = useState<EpsgData[]>([]);
    const [isInfoVisible, setIsInfoVisible] = useState(false);
    const [selectedInfo, setSelectedInfo] = useState<string>("");
    const [rowSelection, setRowSelection] = useState<{ [key: string]: boolean }>({});


    const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
        if (data.length === 0) {
            return [];
        }

        return columnConfig.map(({ key, size }) => {
            if (key === "bbox") {
                return {
                    accessorKey: key,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                    size: size,
                    Cell: ({ row }) => (
                        <span>
                            {Array.isArray(row.original[key])
                                ? row.original[key]
                                    .map((num: number, index: number) =>
                                        index === row.original[key].length - 1
                                            ? num.toLocaleString(undefined, {
                                                maximumFractionDigits: 2,
                                                useGrouping: false,
                                            })
                                            : num.toLocaleString(undefined, {
                                                maximumFractionDigits: 2,
                                                useGrouping: false,
                                            }) + ", "
                                    )
                                    .join("")
                                : row.original[key]}
                        </span>
                    ),
                };
            } else if (key === "wkt") {
                return {
                    accessorKey: key,
                    header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                    size: size,
                    Cell: ({ row }) => (
                        <InfoIcon
                            info={row.original.info}
                            onInfoClick={() => {
                                setSelectedInfo(row.original.wkt);
                                setIsInfoVisible(true);
                            }}
                        />
                    ),
                };
            }

            return {
                accessorKey: key,
                header: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
                size: size,
            };
        });
    }, [data]);

    const table = useMaterialReactTable({
        columns,
        data,
        enableRowNumbers: true,
        enableSorting: false,
        positionToolbarAlertBanner: 'none',
        rowNumberDisplayMode: "static",
        initialState: {
            showGlobalFilter: true,
        },
        getRowId: (row) => row.code,
        muiTableBodyRowProps: ({ row }) => ({
            onClick: () => {
                setRowSelection({ [row.id]: true });
            },
            onDoubleClick: () => handleApply(),
            selected: rowSelection[row.id],
            sx: {
                cursor: 'pointer',
            },
        }),
        onRowSelectionChange: (newSelection) => {
            const selectedRowId = Object.keys(newSelection)[0];
            setRowSelection({ [selectedRowId]: true });
        },
        state: { rowSelection },
    });



    const handleInfoClose = () => {
        setIsInfoVisible(false);
    };

    const handleApply = () => {
        const selectedRowId = Object.keys(rowSelection)[0];
        if (selectedRowId) {
            const selectedData = data.find(row => row.code === selectedRowId);
            if (selectedData) {
                setSelectedRow(selectedData)
            } else {
                console.warn("No matching row found!");
            }
        } else {
            console.warn("No row selected!");
        }
        onClose();
    };

    return (
        <Modal
            title={
                <span style={{ color: titleColor, fontSize: titleFontSize }}>
                    {title}
                </span>
            }
            centered
            open={true}
            onCancel={onClose}
            footer={[
                <button key="apply" onClick={handleApply} className="apply-button">
                    Apply
                </button>,
            ]}
            className="process_modal_search"
        >
            <DataIndexTableSearchableContainer>
                <div className="inputData_table_searchable">
                    <MaterialReactTable table={table} />
                </div>
            </DataIndexTableSearchableContainer>


            <Modal
                title="Info Description"
                open={isInfoVisible}
                onCancel={handleInfoClose}
                footer={null}
                centered
            >
                <p>{selectedInfo}</p>
            </Modal>
        </Modal>
    );
};